import { type FC, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import { nanoid } from 'nanoid'
import { Button } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { type Contact } from 'entities/Contacts/model/Contact'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { LinkContactStore } from 'widgets/ContactsDetails/ui/ContactsIntegrations/ui/LinkContact/store/linkContactStore'
import { useContactsIntegrationsContext } from 'widgets/ContactsDetails/ui/ContactsIntegrations/context/contactsIntegrationsContext'
import { ReconnectButton } from 'widgets/ContactsDetails/ui/ContactsIntegrations/ui/ReconnectButton/ReconnectButton'
import { LinkContactModalContent } from './LinkContactModalContent/LinkContactModalContent'
import { LinkContactModalActions } from './LinkContactModalActions/LinkContactModalActions'
import styles from './styles.module.scss'

type ILinkContactProps = {
  contact: Contact
}

export const LinkContact: FC<ILinkContactProps> = observer(({ contact }) => {
  const store = useContactsIntegrationsContext()
  const entities = useEntitiesContext()
  const linkContactStore = useMemo(() => new LinkContactStore(store, entities), [])
  const { reset, searchMatchedContacts, hasLinkableIntegrationConnected } = linkContactStore

  const hasMatching = (id: number) => {
    return linkContactStore.hasItem(id)
  }

  const matchedCount = (id: number) => {
    return linkContactStore.getItem(id)?.length || 0
  }

  const modalId = nanoid()

  const handleClose = () => {
    modalStore.removeModal(modalId)
    reset()
  }

  const handleLink = () => {
    store.config?.resetActiveAndCloseDetailsIsMobile?.()
    searchMatchedContacts(contact.id, contact.phoneInfo)
    modalStore.addModal({
      id: modalId,
      showCloseButton: false,
      showCloseIcon: true,
      showHeader: true,
      title: 'Link contact',
      paddingTopModal: 60,
      paddingContent: '0px 5px 16px 24px',
      width: 600,
      onClose: handleClose,
      ModalContent: () => (
        <LinkContactModalContent linkContactStore={linkContactStore} contact={contact} />
      ),
      ModalActions: () => (
        <LinkContactModalActions
          linkContactStore={linkContactStore}
          modalId={modalId}
          contact={contact}
          onSuccess={() => {
            store.onSync()
          }}
        />
      ),
    })
  }

  const btnText = hasMatching(contact.id)
    ? `${matchedCount(contact.id)} matching HubSpot contact(s)`
    : 'Link contact'

  useEffect(() => {
    searchMatchedContacts(contact.id, contact.phoneInfo)
  }, [])

  return (
    <div className={classnames(styles.wrap)}>
      {hasLinkableIntegrationConnected ? (
        <Button
          size={'medium'}
          contained={'tertiary'}
          text={btnText}
          icon={'link'}
          fullWidth
          onClick={handleLink}
        />
      ) : (
        <ReconnectButton text={'Connect'} />
      )}
    </div>
  )
})
